/**
 * ============================================================================
 * VINPOP — Favourites System v3.0 (Database-backed via API)
 * ============================================================================
 *
 * Allows logged-in customers to save products as favourites from the
 * /collections/tasting page. Favourites are stored in PostgreSQL database
 * via the VinPOP API (table: public.favorite with wine_id, customer_id).
 *
 * On /collections/tasting & /collections/quizz-tasting:
 *   - Replaces "Add to cart" buttons with "Add to my favorites" ❤️
 *   - If not logged in → redirects to /account/login
 *   - If logged in → saves to database via POST /api/favorite/save
 *
 * On /collections/wishing-list:
 *   - Fetches favourites from database via GET /api/favorite/getAll
 *   - Filters collection to show only favourited products
 *
 * API Endpoints (uses <meta name="api-url">, fallback https://api.vinpop.fr):
 *   - POST /api/favorite/save     {id, customerId}
 *   - DELETE /api/favorite/delete ?id=&customerId=
 *   - GET /api/favorite/getAll    ?customerId=
 *
 * Loaded from: layout/theme.liquid (conditional)
 * ============================================================================
 */

(function () {
  'use strict';

  // Prevent double-loading
  if (window.__vinpopFavouritesLoaded) return;
  window.__vinpopFavouritesLoaded = true;

  // ==================== HIDE GRID IMMEDIATELY ON WISHING-LIST (prevent FOUC) ====================
  if (window.location.pathname.includes('/collections/wishing-list')) {
    const hideStyle = document.createElement('style');
    hideStyle.id = 'vinpop-wishlist-hide';
    hideStyle.textContent = `
      /* Hide the product grid immediately while the paired view loads */
      #facets-results > ul.grid,
      #facets-results > ul,
      .collection .product-grid,
      .collection .product-list,
      .collection [id*="product-grid"],
      .collection .grid--collection,
      #Collection .product-grid,
      [data-collection-container] .product-grid,
      ul.grid.gap-theme {
        opacity: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        pointer-events: none !important;
      }
      /* Also hide filters/sorting that will be removed */
      #native-filters-container,
      #facets-results-count {
        display: none !important;
      }
    `;
    document.head.appendChild(hideStyle);
  }

  // ==================== CONFIG ====================
  const apiUrlMeta = document.querySelector('meta[name="api-url"]');
  const API_BASE = (apiUrlMeta ? apiUrlMeta.getAttribute('content') : 'https://api.vinpop.fr').replace(/\/+$/, '');
  const pathname = window.location.pathname;
  const isTastingCollection = (pathname.includes('/collections/tasting') && !pathname.includes('/collections/tasting-')) || pathname.includes('/collections/quizz-tasting') || pathname.includes('/collections/respond-to-the-quiz-v2') || pathname.includes('/collections/discover-mode-tasting') || pathname.includes('/collections/taste-tunner-tasting') || pathname.includes('/collections/my-playlist-tasting');
  const isQuizOrDiscoverTasting = pathname.includes('/collections/quizz-tasting') || pathname.includes('/collections/respond-to-the-quiz-v2') || pathname.includes('/collections/discover-mode-tasting');
  const isWishingList = pathname.includes('/collections/wishing-list');

  const customerIdMeta = document.querySelector('meta[name="customer-id"]');
  const customerId = customerIdMeta ? customerIdMeta.getAttribute('content') : null;
  let isLoggedIn = !!customerId;

  function getMetaContent(name) {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  }

  function buildVinpopAuthHeaders() {
    const timestamp = getMetaContent('friends-auth-timestamp');
    const signature = getMetaContent('friends-auth-signature');
    if (!customerId || !timestamp || !signature) {
      return {
        'Content-Type': 'application/json'
      };
    }

    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': String(customerId),
      'X-Timestamp': timestamp,
      'X-Signature': signature
    };
  }

  // Guard against stale cached HTML where customer meta is missing while session is still valid.
  async function hasValidShopifySession() {
    if (isLoggedIn) return true;

    try {
      const response = await fetch('/account', {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
        cache: 'no-store'
      });

      const finalUrl = (response && response.url) ? response.url : '';
      const redirectedToLogin = /\/account\/login(?:[/?#]|$)/.test(finalUrl);

      if (response.ok && !redirectedToLogin) {
        // Refresh page once to rehydrate customer meta and keep flows consistent.
        window.location.reload();
        return true;
      }
    } catch (e) {
      console.warn('⚠️ Favourites: Session verification failed', e);
    }

    return false;
  }

  // Logged-out visitors get the shared "log in" modal instead of being thrown
  // straight at /account/login with no explanation.
  async function promptLogin(context) {
    if (window.VinPopLoginPrompt) {
      const allowed = await window.VinPopLoginPrompt.requireLogin({ context: context });
      if (allowed) {
        isLoggedIn = true;
      }
      return allowed;
    }

    // Fallback if the shared module failed to load.
    if (await hasValidShopifySession()) return true;
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = '/account/login?return_to=' + encodeURIComponent(returnUrl);
    return false;
  }

  console.log('❤️ Favourites v3.0: Init | LoggedIn:', isLoggedIn, '| CustomerId:', customerId);

  // ==================== HIDE PRICES ON WISHING-LIST (BLIND TASTING PRODUCTS) ====================
  // Same approach as tasting-recommendations.js - observe DOM and continuously hide prices
  if (isWishingList) {
    (function removeObsoletePlaylistShare() {
      function removeShareBlocks() {
        document.querySelectorAll('.vinpop-playlist-share, .vinpop-share-playlist-btn').forEach(el => {
          const block = el.closest('.vinpop-playlist-share') || el;
          block.remove();
        });
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', removeShareBlocks);
      } else {
        removeShareBlocks();
      }

      const observer = new MutationObserver(removeShareBlocks);
      observer.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 15000);
    })();

    (function hideBlindTastingPrices() {
      function hideAllBlindPrices() {
        // Hide prices in blind tasting tiles (left side of pair)
        document.querySelectorAll('.vinpop-pair-tile--blind [data-product-price]').forEach(el => {
          el.style.setProperty('display', 'none', 'important');
        });
        document.querySelectorAll('.vinpop-pair-tile--blind .price').forEach(el => {
          el.style.setProperty('display', 'none', 'important');
        });
        // Also disable links in blind tiles
        document.querySelectorAll('.vinpop-pair-tile--blind a').forEach(link => {
          link.removeAttribute('href');
          link.style.setProperty('pointer-events', 'none', 'important');
          link.style.cursor = 'default';
        });
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', hideAllBlindPrices);
      } else {
        hideAllBlindPrices();
      }
      // Observe for dynamically added content
      const observer = new MutationObserver(() => hideAllBlindPrices());
      observer.observe(document.documentElement, { childList: true, subtree: true });
      // Stop after 15s 
      setTimeout(() => observer.disconnect(), 15000);
    })();
  }

  // Language detection.
  // English is the universal fallback; only the Dutch (nl) locale renders Dutch,
  // so fr/de/es/it visitors see English rather than Dutch.
  const lang = (document.documentElement.lang || 'nl').toLowerCase().substring(0, 2);
  const isEN = lang !== 'nl';

  const t = {
    addToFav: isEN ? 'Add this one to my favourite' : '❤️ Toevoegen aan mijn playlist',
    removeFromFav: isEN ? '💔 Remove from playlist' : '💔 Verwijderen uit playlist',
    added: isEN ? '✅ Added to your playlist!' : '✅ Toegevoegd aan je playlist!',
    removed: isEN ? 'Removed from playlist' : 'Verwijderd uit playlist',
    loginRequired: isEN ? 'Please log in to save to your playlist' : 'Log in om op te slaan in je playlist',
    noFavourites: isEN ? 'Your playlist is empty.' : 'Je playlist is leeg.',
    noFavouritesDesc: isEN ? 'Browse the <a href="/collections/tasting">tasting collection</a> and click ❤️ to add wines to your playlist!' : 'Bekijk de <a href="/collections/tasting">proeverij collectie</a> en klik op ❤️ om wijnen aan je playlist toe te voegen!',
    yourFavourites: isEN ? 'My Playlist' : 'Mijn Playlist',
    viewProduct: isEN ? 'View' : 'Bekijken',
    remove: isEN ? 'Remove' : 'Verwijderen',
    loginToView: isEN ? 'Please <a href="/account/login">log in</a> to view your playlist.' : '<a href="/account/login">Log in</a> om je playlist te bekijken.',
    loading: isEN ? 'Loading your playlist...' : 'Je playlist laden...',
    itemCount: isEN ? ' wine(s) in your playlist' : ' wijn(en) in je playlist',
    addedToFav: isEN ? 'I do like it' : 'In playlist ❤️',
    addedOn: isEN ? 'Added on' : 'Toegevoegd op',
    vendor: isEN ? 'by' : 'van',
    loveIt: isEN ? 'Love it' : 'Vind ik lekker',
    loveItText: isEN ? 'Love it ❤️' : 'Vind ik lekker ❤️',
    addRealWine: isEN ? '🛒 Add the real wine to cart' : '🛒 Voeg de echte wijn toe aan winkelwagen',
    revealAfterTasting: isEN ? 'Reveal after tasting!' : 'Ontdek na de proeverij!',
    adding: isEN ? '⏳ Adding…' : '⏳ Toevoegen…',
    addedToCart: isEN ? '✅ Added to cart!' : '✅ Toegevoegd aan winkelwagen!',
    cartError: isEN ? 'Error' : 'Fout',
    tryAgain: isEN ? '❌ Try again' : '❌ Probeer opnieuw',
    findingWine: isEN ? '⏳ Finding wine…' : '⏳ Wijn zoeken…',
    notAvailable: isEN ? '❌ Not available' : '❌ Niet beschikbaar',
  };

  // ==================== API FUNCTIONS ====================
  // Cache for favourites (to avoid repeated API calls)
  let favouritesCache = null;
  let cacheLoaded = false;
  let lovedRatingsCache = null;
  let lovedRatingsLoaded = false;

  function normalizeFavouriteIds(data) {
    if (!Array.isArray(data)) return [];

    return data
      .map(item => {
        if (item == null) return '';
        if (typeof item === 'object') {
          return item.wine_id || item.id || item.product_id || item.productId || '';
        }
        return item;
      })
      .map(id => String(id || '').trim())
      .filter(Boolean);
  }

  async function loadFavouritesFromAPI() {
    if (!isLoggedIn) return [];
    if (cacheLoaded && favouritesCache !== null) return favouritesCache;

    try {
      const response = await fetch(`${API_BASE}/api/favorite/getAll?customerId=${customerId}`, {
        headers: buildVinpopAuthHeaders(),
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      // API can return ["123"] or [{ wine_id: "123" }] depending on backend version.
      favouritesCache = normalizeFavouriteIds(data);
      cacheLoaded = true;
      // Expose cache globally so other scripts (tasting-recommendations.js) can check favourite state
      window.__vinpopFavouritesCache = favouritesCache;
      console.log('❤️ Favourites: Loaded from DB:', favouritesCache.length, 'items');
      return favouritesCache;
    } catch (e) {
      console.error('❌ Favourites: Error loading from API', e);
      return [];
    }
  }

  async function addFavouriteToAPI(productId) {
    if (!isLoggedIn) return false;
    try {
      const response = await fetch(`${API_BASE}/api/favorite/save`, {
        method: 'POST',
        headers: buildVinpopAuthHeaders(),
        body: JSON.stringify({ id: String(productId), customerId: String(customerId) })
      });
      if (!response.ok) throw new Error('API error');
      // Update cache
      if (favouritesCache && !favouritesCache.includes(String(productId))) {
        favouritesCache.push(String(productId));
      }
      window.__vinpopFavouritesCache = favouritesCache;
      console.log('❤️ Favourites: Added to DB:', productId);
      return true;
    } catch (e) {
      console.error('❌ Favourites: Error adding to API', e);
      return false;
    }
  }

  async function removeFavouriteFromAPI(productId) {
    if (!isLoggedIn) return false;
    try {
      const response = await fetch(`${API_BASE}/api/favorite/delete?id=${String(productId)}&customerId=${String(customerId)}`, {
        method: 'DELETE',
        headers: buildVinpopAuthHeaders()
      });
      if (!response.ok) throw new Error('API error');
      // Update cache
      if (favouritesCache) {
        favouritesCache = favouritesCache.filter(id => id !== String(productId));
      }
      window.__vinpopFavouritesCache = favouritesCache;
      console.log('💔 Favourites: Removed from DB:', productId);
      return true;
    } catch (e) {
      console.error('❌ Favourites: Error removing from API', e);
      return false;
    }
  }

  function isFavouriteCached(productId) {
    if (!favouritesCache) return false;
    return favouritesCache.includes(String(productId || ''));
  }

  function getCatalogWineId(productId) {
    const id = String(productId || '');
    if (!id) return '';

    const discoverCatalog = window.discoverTastingCatalog || {};
    const quizzCatalog = window.quizzTastingCatalog || {};
    const tastingCatalog = window.tastingCatalog || {};
    const shopCatalog = window.shopCatalog || {};
    const shopCatalogIndex = window.shopCatalogIndex || {};

    return (
      (discoverCatalog[id] && (discoverCatalog[id].productidmatch || discoverCatalog[id].productid)) ||
      (quizzCatalog[id] && (quizzCatalog[id].productidmatch || quizzCatalog[id].productid)) ||
      (tastingCatalog[id] && (tastingCatalog[id].productidmatch || tastingCatalog[id].productid)) ||
      (shopCatalog[id] && shopCatalog[id].relatedProductId) ||
      (shopCatalogIndex[id] && shopCatalogIndex[id].r) ||
      ''
    );
  }

  async function loadLovedRatingsFromAPI() {
    if (!isLoggedIn) return new Set();
    if (lovedRatingsLoaded && lovedRatingsCache !== null) return lovedRatingsCache;

    try {
      const response = await fetch(`${API_BASE}/api/ratings/getAll?customerId=${customerId}`, {
        headers: buildVinpopAuthHeaders()
      });
      if (!response.ok) throw new Error('API error');
      const data = await response.json();
      lovedRatingsCache = new Set(
        Array.isArray(data)
          ? data
              .filter(item => String(item.rating) === '3')
              .map(item => String(item.id))
          : []
      );
      lovedRatingsLoaded = true;
      console.log('❤️ Ratings: Loaded loved products:', lovedRatingsCache.size);
      return lovedRatingsCache;
    } catch (e) {
      console.error('❌ Ratings: Error loading from API', e);
      return new Set();
    }
  }

  function isLovedCached(productId) {
    return lovedRatingsCache ? lovedRatingsCache.has(String(productId)) : false;
  }

  async function saveLoveRatingToAPI(productId) {
    if (!isLoggedIn) return false;
    try {
      const response = await fetch(`${API_BASE}/api/ratings/save`, {
        method: 'POST',
        headers: buildVinpopAuthHeaders(),
        body: JSON.stringify({ id: String(productId), customerId: String(customerId), rating: 3 })
      });
      if (!response.ok) throw new Error('API error');
      if (!lovedRatingsCache) lovedRatingsCache = new Set();
      lovedRatingsCache.add(String(productId));
      return true;
    } catch (e) {
      console.error('❌ Ratings: Error saving love rating', e);
      return false;
    }
  }

  async function deleteLoveRatingFromAPI(productId) {
    if (!isLoggedIn) return false;
    try {
      const response = await fetch(`${API_BASE}/api/ratings/delete?customerId=${String(customerId)}&id=${String(productId)}`, {
        method: 'DELETE',
        headers: buildVinpopAuthHeaders()
      });
      if (!response.ok) throw new Error('API error');
      if (lovedRatingsCache) lovedRatingsCache.delete(String(productId));
      return true;
    } catch (e) {
      console.error('❌ Ratings: Error deleting love rating', e);
      return false;
    }
  }

  function resolveLoveTargetProductIds(productId) {
    const id = String(productId || '');
    if (!id) return [];

    const candidates = [];
    const addCandidate = (value) => {
      const v = String(value || '');
      if (!v) return;
      if (!candidates.includes(v)) candidates.push(v);
    };

    // Resolve the matching key via tasting metafields.
    // This key is productidmatch/productid and is the canonical wine API ID.
    const discoverCatalog = window.discoverTastingCatalog || {};
    const quizzCatalog = window.quizzTastingCatalog || {};
    const tastingCatalog = window.tastingCatalog || {};
    const shopCatalog = window.shopCatalog || {};
    const shopCatalogIndex = window.shopCatalogIndex || {};

    const matchKey = String(getCatalogWineId(id) || '');

    // The ratings API expects the wine ID from productidmatch/productid.
    addCandidate(matchKey);

    // 1) Prefer official Shopify products from full shopCatalog (price > 0 usually means real wine).
    if (matchKey) {
      Object.keys(shopCatalog).forEach((shopifyId) => {
        const entry = shopCatalog[shopifyId] || {};
        if (String(entry.relatedProductId || '') !== String(matchKey)) return;

        const isSourceId = String(shopifyId) === id;
        const hasPrice = Number(entry.price || 0) > 0;

        if (!isSourceId && hasPrice) addCandidate(shopifyId);
      });

      // 2) Then include any other mapped IDs from full catalog.
      Object.keys(shopCatalog).forEach((shopifyId) => {
        const entry = shopCatalog[shopifyId] || {};
        if (String(entry.relatedProductId || '') !== String(matchKey)) return;
        if (String(shopifyId) === id) return;
        addCandidate(shopifyId);
      });

      // 3) Fallback to lightweight index mapping.
      Object.keys(shopCatalogIndex).forEach((shopifyId) => {
        if (shopifyId === '_') return;
        const entry = shopCatalogIndex[shopifyId] || {};
        if (String(entry.r || '') !== String(matchKey)) return;
        if (String(shopifyId) === id) return;
        addCandidate(shopifyId);
      });

      // 4) If matchKey itself looks like a Shopify product ID present in the catalog
      //    (case where productidmatch is a direct reference to a test-kit product),
      //    use it directly.
      if (matchKey !== id) {
        if (shopCatalog[matchKey] || shopCatalogIndex[matchKey]) {
          addCandidate(matchKey);
        }
      }
    }

    // 5) If clicked product is already an official wine product, keep it as fallback.
    addCandidate(id);

    return candidates;
  }

  async function saveLoveWithFallback(productId) {
    const targetIds = resolveLoveTargetProductIds(productId);
    if (targetIds.length === 0) return false;

    console.log('❤️ Favourites: Love it sync targets for', productId, targetIds);

    for (const targetId of targetIds) {
      const loveSaved = await saveLoveRatingToAPI(targetId);
      if (!loveSaved) continue;

      // Update any visible Love buttons for this product ID.
      document.querySelectorAll(`.product-rating__btn--love[data-wishlist-love="true"][data-product-id="${targetId}"]`).forEach(btn => {
        btn.classList.add('selected');
      });

      return true;
    }

    return false;
  }

  async function deleteLoveWithFallback(productId) {
    const targetIds = resolveLoveTargetProductIds(productId);
    if (targetIds.length === 0) return false;

    console.log('💔 Favourites: Love it removal targets for', productId, targetIds);

    let removed = false;

    for (const targetId of targetIds) {
      const loveDeleted = await deleteLoveRatingFromAPI(targetId);
      if (!loveDeleted) continue;

      removed = true;

      document.querySelectorAll(`.product-rating__btn--love[data-wishlist-love="true"][data-product-id="${targetId}"]`).forEach(btn => {
        btn.classList.remove('selected');
      });
    }

    return removed;
  }

  // ==================== DISABLE PRODUCT LINKS ON QUIZZ-TASTING ====================
  const isQuizzTasting = pathname.includes('/collections/quizz-tasting') || pathname.includes('/collections/respond-to-the-quiz-v2');
  if (isQuizzTasting) {
    console.log('🔒 Favourites: Disabling product card links on quizz-tasting...');

    function disableProductLinks() {
      // Only disable links pointing to /products/ — allow redirects to /collections/discover-mode-tasting?source_product=...
      document.querySelectorAll('.product-tile a[href*="/products/"]').forEach(link => {
        if (link.closest('.vinpop-tasting-see-wrapper') || link.classList.contains('vinpop-tasting-cart-btn')) {
          link.style.pointerEvents = '';
          link.style.cursor = '';
          link.removeAttribute('tabindex');
          return;
        }

        link.style.pointerEvents = 'none';
        link.style.cursor = 'default';
        link.setAttribute('tabindex', '-1');
        link.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
        }, true);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(disableProductLinks, 200));
    } else {
      setTimeout(disableProductLinks, 200);
    }

    // Re-apply after AJAX filtering / DOM changes
    const quizzObserver = new MutationObserver(() => setTimeout(disableProductLinks, 100));
    const quizzGrid = document.querySelector('#facets-results');
    if (quizzGrid) {
      quizzObserver.observe(quizzGrid, { childList: true, subtree: true });
    }
    // Also run periodically for first few seconds (quiz may inject products)
    let quizzCheckCount = 0;
    const quizzInterval = setInterval(() => {
      disableProductLinks();
      quizzCheckCount++;
      if (quizzCheckCount > 20) clearInterval(quizzInterval);
    }, 500);
  }

  // ==================== GLOBAL CLICK HANDLER FOR FAVOURITE BUTTONS ====================
  document.addEventListener('click', async function (e) {
    const loveBtn = e.target.closest('.product-rating__btn--love[data-wishlist-love="true"]');
    if (loveBtn) {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        await promptLogin();
        return;
      }

      const productId = loveBtn.getAttribute('data-product-id');
      if (!productId) return;

      const wasSelected = loveBtn.classList.contains('selected');
      loveBtn.disabled = true;
      loveBtn.style.opacity = '0.6';

      if (wasSelected) {
        const success = await deleteLoveRatingFromAPI(productId);
        if (success) {
          document.querySelectorAll(`.product-rating__btn--love[data-wishlist-love="true"][data-product-id="${productId}"]`).forEach(btn => {
            btn.classList.remove('selected');
          });
          showToast(isEN ? 'Removed from Love it' : 'Verwijderd uit Love it');
        }
      } else {
        const success = await saveLoveRatingToAPI(productId);
        if (success) {
          document.querySelectorAll(`.product-rating__btn--love[data-wishlist-love="true"][data-product-id="${productId}"]`).forEach(btn => {
            btn.classList.add('selected');
          });
          showToast(isEN ? 'Added to Love it ❤️' : 'Toegevoegd aan Love it ❤️');
        }
      }

      loveBtn.disabled = false;
      loveBtn.style.opacity = '1';
      return;
    }

    const favBtn = e.target.closest('.vinpop-fav-btn');
    if (!favBtn) return;

    e.preventDefault();
    e.stopPropagation();

    // Must be logged in
    if (!isLoggedIn) {
      await promptLogin('playlist');
      return;
    }

    const productId = favBtn.getAttribute('data-product-id');
    const favouriteId = String(productId || '');

    // Disable button during API call
    favBtn.disabled = true;
    favBtn.style.opacity = '0.6';

    if (isFavouriteCached(productId)) {
      // Remove from favourites
      const success = await removeFavouriteFromAPI(favouriteId);
      if (success) {
        const shouldSyncLoveRemoval = isQuizOrDiscoverTasting || isWishingList;
        if (shouldSyncLoveRemoval) {
          const loveRemoved = await deleteLoveWithFallback(productId);
          if (!loveRemoved) {
            console.warn('⚠️ Favourites: could not remove Love it for product', productId);
          }
        }

        favBtn.classList.remove('vinpop-fav-btn--active');
        favBtn.querySelector('.vinpop-fav-btn__text').textContent = t.addToFav;
        showToast(t.removed);

        // On wishing-list: remove the entire pair row with animation
        if (isWishingList) {
          const pairRow = favBtn.closest('.vinpop-pair-row');
          if (pairRow) {
            pairRow.style.transition = 'opacity 0.4s, transform 0.4s';
            pairRow.style.opacity = '0';
            pairRow.style.transform = 'scale(0.95)';
            setTimeout(() => {
              pairRow.remove();
              // If no more pairs, show empty message
              const grid = document.querySelector('.vinpop-paired-grid');
              if (grid && grid.children.length === 0) {
                grid.innerHTML = '<div style="text-align:center;padding:3rem 1rem;"><p style="font-size:1.2rem;font-weight:600;">' +
                  (isEN ? '🎵 Your playlist is empty' : '🎵 Je playlist is leeg') +
                  '</p><p style="margin-top:0.5rem;opacity:0.7;">' +
                  (isEN ? 'Browse the <a href="/collections/tasting">tasting collection</a> and click ❤️ to add wines!' : 'Bekijk de <a href="/collections/tasting">proeverij collectie</a> en klik op ❤️ om wijnen toe te voegen!') +
                  '</p></div>';
              }
            }, 400);
          }
        }
      }
    } else {
      // Add to favourites
      const success = await addFavouriteToAPI(favouriteId);
      if (success) {
        favBtn.classList.add('vinpop-fav-btn--active');
        favBtn.querySelector('.vinpop-fav-btn__text').textContent = t.addedToFav;
        showToast(t.added);

        // On quizz/discover tasting flows, adding to playlist should also mark as "Love it".
        if (isQuizOrDiscoverTasting) {
          const loveSaved = await saveLoveWithFallback(productId);
          if (!loveSaved) {
            console.warn('⚠️ Favourites: could not sync Love it for product', productId);
          }
        }

        // Heart animation
        favBtn.classList.add('vinpop-fav-btn--pulse');
        setTimeout(() => favBtn.classList.remove('vinpop-fav-btn--pulse'), 600);
      }
    }

    // Re-enable button
    favBtn.disabled = false;
    favBtn.style.opacity = '1';
  });

  // ==================== TASTING COLLECTION ====================
  if (isTastingCollection) {
    console.log('❤️ Favourites: Initializing on tasting collection...');

    function replaceAddToCartButtons() {
      // Find all quick-buy containers
      const quickBuyContainers = document.querySelectorAll('.product-tile__quick-buy');

      quickBuyContainers.forEach(container => {
        // Check if already replaced
        if (container.querySelector('.vinpop-fav-btn')) return;

        // Find the product tile parent
        const productTile = container.closest('[data-product-id]');
        if (!productTile) return;

        const productId = productTile.getAttribute('data-product-id');

        // Get product info from the tile
        const titleEl = productTile.querySelector('h3, h2');
        const productTitle = titleEl ? titleEl.textContent.trim() : '';
        const imageEl = productTile.querySelector('.product-tile__featured-media img');
        const productImage = imageEl ? imageEl.getAttribute('src') : '';
        const linkEl = productTile.querySelector('a[href*="/products/"]');
        const productHandle = linkEl ? linkEl.getAttribute('href').split('/products/')[1]?.split('?')[0] : '';

        // Check if this product is already a favourite (from cache)
        const isAlreadyFav = isFavouriteCached(productId);

        // Hide the original form content
        const originalForm = container.querySelector('.product-quick-buy__container');
        if (originalForm) {
          originalForm.style.display = 'none';
        }

        // Create favourite button
        const favBtnWrapper = document.createElement('div');
        favBtnWrapper.className = 'vinpop-fav-btn-wrapper';
        favBtnWrapper.innerHTML = `
          <button
            class="vinpop-fav-btn ${isAlreadyFav ? 'vinpop-fav-btn--active' : ''}"
            data-product-id="${productId}"
            data-product-handle="${productHandle}"
            data-product-title="${productTitle.replace(/"/g, '&quot;')}"
            data-product-image="${productImage}"
            type="button"
          >
            <span class="vinpop-fav-btn__text">${isAlreadyFav ? t.addedToFav : t.addToFav}</span>
          </button>
        `;

        container.appendChild(favBtnWrapper);
      });
    }

    // Run replacement after DOM is ready and observe for dynamic changes
    async function init() {
      // Load favourites from API first (to know which products are already favourited)
      if (isLoggedIn) {
        await loadFavouritesFromAPI();
      }
      replaceAddToCartButtons();

      // Watch for DOM changes (facets/AJAX filtering may reload products)
      const observer = new MutationObserver(() => {
        setTimeout(replaceAddToCartButtons, 100);
      });
      const grid = document.querySelector('#facets-results');
      if (grid) {
        observer.observe(grid, { childList: true, subtree: true });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(init, 300));
    } else {
      setTimeout(init, 300);
    }
  }


  // ==================== WISHING LIST COLLECTION ====================
  if (isWishingList) {
    console.log('❤️ Favourites v3.1: Initializing wishing-list with paired products...');

    let wishFilterRunning = false;
    let favIdsFromDB = null;
    let pairedViewRendered = false;

    // Detect locale prefix from current URL (e.g., '/en/' or '/nl/')
    function getLocalePrefix() {
      const match = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
      return match ? `/${match[1]}` : '';
    }

    async function fetchProductTile(productHandle) {
      if (!productHandle) return null;
      try {
        const localePrefix = getLocalePrefix();
        const resp = await fetch(`${localePrefix}/products/${productHandle}?sections=product-tile-render`);
        if (!resp.ok) return null;
        const json = await resp.json();
        let html = json['product-tile-render'];
        if (!html) return null;
        // Strip shopify-section wrapper
        html = html.replace(/<div[^>]*id="shopify-section-[^"]*"[^>]*class="shopify-section"[^>]*>/gi, '')
                   .replace(/<\/div>\s*$/i, '');
        return html.trim();
      } catch (e) {
        console.error('❌ Error fetching product tile:', productHandle, e);
        return null;
      }
    }

    function getProductTileFromPage(productId) {
      const id = String(productId || '');
      if (!id) return null;

      const tile =
        document.querySelector('.product-tile[data-product-id="' + id + '"]') ||
        document.querySelector('[data-product-id="' + id + '"] .product-tile') ||
        document.querySelector('[data-product-id="' + id + '"]');

      if (!tile) return null;
      return tile.outerHTML ? tile.outerHTML.trim() : null;
    }

    async function filterWishingList() {
      if (wishFilterRunning) return;
      wishFilterRunning = true;

      console.log('❤️ Wishing-list: filtering products. LoggedIn:', isLoggedIn, 'CustomerId:', customerId);

      const grid = document.querySelector('#facets-results ul.grid') ||
                   document.querySelector('ul.grid.gap-theme');

      if (!grid) {
        console.log('❤️ Wishing-list: grid not found yet');
        wishFilterRunning = false;
        return;
      }

      // If already rendered, just keep grid hidden and exit
      if (pairedViewRendered) {
        grid.style.display = 'none';
        wishFilterRunning = false;
        return;
      }

      // Remove any previously injected messages (NOT paired-grid since we check pairedViewRendered first)
      document.querySelectorAll('.vinpop-wishing-msg').forEach(el => el.remove());

      if (!isLoggedIn) {
        const hasSession = await hasValidShopifySession();
        if (hasSession) {
          wishFilterRunning = false;
          return;
        }
        grid.style.display = 'none';
        const msg = document.createElement('div');
        msg.className = 'vinpop-wishing-msg';
        msg.style.cssText = 'text-align:center;padding:3rem 1rem;';
        msg.innerHTML = '<div style="font-size:2rem;margin-bottom:1rem;">🔒</div><p>' + t.loginToView + '</p>';
        grid.parentNode.insertBefore(msg, grid);
        wishFilterRunning = false;
        return;
      }

      // Fetch favourites from database (only once)
      if (favIdsFromDB === null) {
        const lovedRatingsPromise = loadLovedRatingsFromAPI();

        try {
          const response = await fetch(`${API_BASE}/api/favorite/getAll?customerId=${customerId}`, {
            headers: buildVinpopAuthHeaders(),
            cache: 'no-store'
          });
          if (!response.ok) throw new Error('API error');
          const data = await response.json();
          favIdsFromDB = new Set(normalizeFavouriteIds(data));
          // Sync with favouritesCache so isFavouriteCached() works for click handler
          favouritesCache = [...favIdsFromDB];
          cacheLoaded = true;
          window.__vinpopFavouritesCache = favouritesCache;
          console.log('❤️ Wishing-list: Loaded favourites from DB:', favIdsFromDB.size, 'items');
        } catch (e) {
          console.error('❌ Wishing-list: Error loading favourites from API', e);
          favIdsFromDB = new Set();
        }

        await lovedRatingsPromise;
      }

      console.log('❤️ Wishing-list: favourites IDs:', [...favIdsFromDB]);

      // Debug: What products are actually on this page?
      const gridItems = document.querySelectorAll('[data-product-id]');
      const pageProductIds = [...gridItems].map(el => el.getAttribute('data-product-id'));
      console.log('❤️ Wishing-list: Products on THIS PAGE (grid):', pageProductIds);

      if (favIdsFromDB.size === 0) {
        grid.style.display = 'none';
        const msg = document.createElement('div');
        msg.className = 'vinpop-wishing-msg';
        msg.style.cssText = 'text-align:center;padding:3rem 1rem;';
        msg.innerHTML = '<div style="font-size:2rem;margin-bottom:1rem;">💔</div>' +
          '<p style="font-size:1.1rem;font-weight:600;">' + t.noFavourites + '</p>' +
          '<p style="margin-top:0.5rem;">' + t.noFavouritesDesc + '</p>';
        grid.parentNode.insertBefore(msg, grid);
        wishFilterRunning = false;
        return;
      }


      // Build paired view: [Blind tasting product] → [Real wine product]
      const catalog = window.shopCatalog || {};
      const catalogIndex = window.shopCatalogIndex || {};
      const catalogKeys = Object.keys(catalog);
      console.log('❤️ Wishing-list: Catalog has', catalogKeys.length, 'products');
      console.log('❤️ Wishing-list: Index has', Object.keys(catalogIndex).length - 1, 'products');
      console.log('❤️ Wishing-list: Looking for IDs:', [...favIdsFromDB]);
      
      // Helper: resolve product data from catalog, index, or AJAX
      function resolveFromIndex(productId) {
        const idx = catalogIndex[productId];
        if (!idx) return null;
        // Build a minimal catalog-compatible entry from the lightweight index
        return {
          id: productId,
          handle: idx.h,
          title: idx.t || '',
          relatedProductId: idx.r,
          _fromIndex: true
        };
      }

      function findRealWineByWineId(wineId) {
        const key = String(wineId || '');
        if (!key) return null;

        let fallback = null;
        for (const productId of catalogKeys) {
          const product = catalog[productId] || {};
          if (String(product.relatedProductId || '') !== key) continue;

          const hasPrice = Number(product.price || 0) > 0;
          if (hasPrice) return product;
          if (!fallback) fallback = product;
        }

        for (const productId of Object.keys(catalogIndex)) {
          if (productId === '_') continue;
          const indexed = catalogIndex[productId] || {};
          if (String(indexed.r || '') !== key) continue;

          const product = resolveFromIndex(productId);
          if (!fallback) fallback = product;
        }

        return fallback;
      }

      const pairs = [];

      for (const favId of favIdsFromDB) {
        // Try full catalog first (has complete product data)
        let productData = catalog[favId];
        
        // If not found by product ID, check if it's a variant ID
        if (!productData) {
          for (const key of catalogKeys) {
            const prod = catalog[key];
            if (prod.variants && prod.variants.some(v => String(v.id) === String(favId))) {
              console.log('❤️ Wishing-list: Found', favId, 'as variant of product', key);
              productData = prod;
              break;
            }
          }
        }
        
        // FALLBACK: check lightweight index (covers ALL wishing-list products)
        if (!productData) {
          productData = resolveFromIndex(favId);
          if (productData) {
            console.log('❤️ Wishing-list: Found', favId, 'in lightweight index:', productData.title);
          }
        }

        if (!productData) {
          // Last resort: try to find it in the page DOM and get handle
          const domEl = document.querySelector('[data-product-id="' + favId + '"]');
          if (domEl) {
            const link = domEl.querySelector('a[href*="/products/"]');
            if (link) {
              const match = link.href.match(/\/products\/([^/?#]+)/);
              if (match) {
                console.log('❤️ Wishing-list: Found', favId, 'via DOM, handle:', match[1]);
                productData = { id: favId, handle: match[1], title: '', relatedProductId: null, _fromDOM: true };
              }
            }
          }
        }
        
        if (!productData) {
          console.log('❤️ Wishing-list: Product truly not found anywhere:', favId);
          continue;
        }

        if (Number(productData.price || 0) > 0) {
          console.warn('Wishing-list: Skipping non-tasting favourite product:', favId, productData.title);
          continue;
        }

        const relatedId = productData.relatedProductId;
        console.log('❤️ Wishing-list: Product', favId, '→ relatedProductId:', relatedId, '| title:', productData.title);
        
        // Try full catalog for related product
        let relatedData = relatedId ? catalog[relatedId] : null;
        if (!relatedData && relatedId) {
          relatedData = catalog[String(relatedId)];
        }
        // Fallback: check index for related product too
        if (!relatedData && relatedId) {
          relatedData = resolveFromIndex(String(relatedId));
          if (relatedData) {
            console.log('❤️ Wishing-list: Related product found in index:', relatedData.title);
          }
        }
        
        console.log('❤️ Wishing-list: Related product found:', relatedData ? relatedData.title : 'NOT FOUND');

        if (!relatedData && relatedId) {
          relatedData = findRealWineByWineId(String(relatedId));
          if (relatedData) {
            console.log('Wishing-list: Related product found via relatedProductId:', relatedData.title);
          }
        }

        pairs.push({
          blind: productData,
          real: relatedData
        });
      }

      console.log('❤️ Wishing-list: Building paired view for', pairs.length, 'items');

      // Wrap tile fetching in try-catch for safety
      try {
        // Fetch product tiles for all products in parallel
        const tilePromises = [];
        const tileMap = {};

        for (const pair of pairs) {
          if (pair.blind && pair.blind.handle) {
            const existingBlindTile = getProductTileFromPage(pair.blind.id);
            if (existingBlindTile) {
              tileMap[pair.blind.id] = existingBlindTile;
            } else {
              console.log('Wishing-list: Fetching tile for blind:', pair.blind.handle);
              tilePromises.push(
                fetchProductTile(pair.blind.handle).then(html => {
                  console.log('Wishing-list: Got tile for', pair.blind.handle, ':', html ? html.substring(0, 100) + '...' : 'NULL');
                  tileMap[pair.blind.id] = html;
                }).catch(err => {
                  console.error('Wishing-list: Failed to fetch tile for', pair.blind.handle, err);
                })
              );
            }
          }

          if (pair.real && pair.real.handle) {
            const existingRealTile = getProductTileFromPage(pair.real.id);
            if (existingRealTile) {
              tileMap[pair.real.id] = existingRealTile;
            } else {
              console.log('Wishing-list: Fetching tile for real:', pair.real.handle);
              tilePromises.push(
                fetchProductTile(pair.real.handle).then(html => {
                  console.log('Wishing-list: Got tile for', pair.real.handle, ':', html ? html.substring(0, 100) + '...' : 'NULL');
                  tileMap[pair.real.id] = html;
                }).catch(err => {
                  console.error('Wishing-list: Failed to fetch tile for', pair.real.handle, err);
                })
              );
            }
          }
        }

        console.log('Wishing-list: Local tiles:', Object.keys(tileMap).length, '| network fetches:', tilePromises.length);
        console.log('Wishing-list: Waiting for', tilePromises.length, 'tile fetches...');
        await Promise.all(tilePromises);
        console.log('❤️ Wishing-list: All tiles fetched. TileMap keys:', Object.keys(tileMap));

        // Check if we got any tiles
        const tilesLoaded = Object.keys(tileMap).filter(k => tileMap[k]).length;
        if (tilesLoaded === 0) {
          console.error('❌ Wishing-list: No tiles loaded, showing fallback');
          throw new Error('No tiles loaded');
        }

      // Build the paired HTML
      let pairedHTML = '<div class="vinpop-paired-grid">';
      
      for (const pair of pairs) {
        let blindTile = pair.blind ? tileMap[pair.blind.id] : null;
        let realTile = pair.real ? tileMap[pair.real.id] : null;

        // Clean blind tile: remove price, quick-buy, and disable links
        if (blindTile) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = blindTile;
          // Remove all price elements - try multiple approaches
          // 1. By class
          tempDiv.querySelectorAll('.price, .price__container, .price-item, [data-product-price]').forEach(el => el.remove());
          // 2. By class attribute containing "price"
          tempDiv.querySelectorAll('[class*="price"]').forEach(el => el.remove());
          // 3. Find any element with €0 text and remove its container
          tempDiv.querySelectorAll('span, div').forEach(el => {
            if (el.textContent.includes('€0,00') || el.textContent.includes('€0.00')) {
              // Find parent with price class or remove the element's closest div parent
              const priceParent = el.closest('.price') || el.closest('div[class*="price"]') || el.parentElement;
              if (priceParent && priceParent !== tempDiv) priceParent.remove();
            }
          });
          // Remove quick-buy form (blind products don't have add to cart)
          tempDiv.querySelectorAll('.product-tile__quick-buy, .product-quick-buy__container').forEach(el => el.remove());
          // Remove href from links (make non-clickable)
          tempDiv.querySelectorAll('a').forEach(link => {
            link.removeAttribute('href');
            link.style.pointerEvents = 'none';
            link.style.cursor = 'default';
          });
          blindTile = tempDiv.innerHTML;
          console.log('❤️ Blind tile cleaned, price + quick-buy removed');
        }

        // Clean real tile on wishing-list: remove native quick-buy row (qty + add to cart)
        if (realTile) {
          const tempRealDiv = document.createElement('div');
          tempRealDiv.innerHTML = realTile;
          tempRealDiv.querySelectorAll('.product-tile__quick-buy').forEach(el => el.remove());
          realTile = tempRealDiv.innerHTML;
        }

        pairedHTML += '<div class="vinpop-pair-row">';
        
        // Blind tasting product (left) - with favorite button and delivery info
        pairedHTML += '<div class="vinpop-pair-item vinpop-pair-blind">';
        pairedHTML += '<div class="vinpop-pair-label">🎭 ' + (isEN ? 'Your tasting' : 'Je proeverij') + '</div>';
        if (blindTile) {
          // Wrap tile and add favorite button + delivery reassurance
          pairedHTML += '<div class="vinpop-pair-tile vinpop-pair-tile--blind" data-product-id="' + pair.blind.id + '">';
          pairedHTML += blindTile;
          // Add favorite button (already in playlist)
          pairedHTML += `
            <div class="product-tile__quick-buy" style="padding: 0 1rem;">
              <div class="vinpop-fav-btn-wrapper">
                <button class="vinpop-fav-btn vinpop-fav-btn--active" 
                  data-product-id="${pair.blind.id}" 
                  data-product-handle="${pair.blind.handle}" 
                  data-product-title="${(pair.blind.title || '').replace(/"/g, '&quot;')}" 
                  type="button">
                  <span class="vinpop-fav-btn__text">${t.addedToFav}</span>
                </button>
              </div>
            </div>
          `;
          pairedHTML += '</div>';
        } else {
          pairedHTML += '<div class="vinpop-pair-placeholder">' + (pair.blind ? pair.blind.title : 'Unknown') + '</div>';
        }
        pairedHTML += '</div>';

        // Arrow
        pairedHTML += '<div class="vinpop-pair-arrow">→</div>';

        // Real wine product (right)
        pairedHTML += '<div class="vinpop-pair-item vinpop-pair-real">';
        pairedHTML += '<div class="vinpop-pair-label">🍷 ' + (isEN ? 'The wine' : 'De wijn') + '</div>';
        if (realTile) {
          const isLoved = pair.real ? isLovedCached(pair.real.id) : false;
          // Resolve best variant ID for cart button (synchronous)
          let realVariantId = '';
          if (pair.real && pair.real.variants && pair.real.variants.length) {
            const av = pair.real.variants.find(v => v.available) || pair.real.variants[0];
            if (av) realVariantId = String(av.id);
          }
          pairedHTML += '<div class="vinpop-pair-tile">' + realTile;
          if (pair.real) {
            pairedHTML += `
              <div class="product-tile__quick-buy" style="padding: 0 1rem; margin-top: .5rem;">
                <div class="vinpop-fav-btn-wrapper vinpop-love-btn-wrapper">
                  <button type="button" class="vinpop-fav-btn vinpop-love-btn product-rating__btn--love ${isLoved ? 'selected' : ''}" data-wishlist-love="true" data-rating="love" data-product-id="${pair.real.id}" aria-label="${t.loveIt}">
                    <span class="vinpop-fav-btn__text">${t.loveItText}</span>
                  </button>
                </div>
                <div class="vinpop-fav-btn-wrapper vinpop-wishing-cart-wrapper">
                  <button type="button" class="vinpop-tasting-cart-btn vinpop-wishing-cart-btn"
                    data-variant-id="${realVariantId}"
                    data-product-handle="${pair.real.handle || ''}">
                    ${t.addRealWine}
                  </button>
                </div>
              </div>
            `;
          }
          pairedHTML += '</div>';
        } else {
          pairedHTML += '<div class="vinpop-pair-placeholder vinpop-pair-mystery">❓ ' + t.revealAfterTasting + '</div>';
        }
        pairedHTML += '</div>';

        pairedHTML += '</div>'; // .vinpop-pair-row
      }

      pairedHTML += '</div>'; // .vinpop-paired-grid


      console.log('❤️ Wishing-list: Paired HTML built, length:', pairedHTML.length);

      // Inject paired view directly.
      const pairedContainer = document.createElement('div');
      pairedContainer.innerHTML = pairedHTML;
      
      // Attach cart button handlers before moving nodes to the live DOM
      pairedContainer.querySelectorAll('.vinpop-wishing-cart-btn').forEach(btn => {
        btn.addEventListener('click', function() {
          const variantId = btn.getAttribute('data-variant-id');
          const productHandle = btn.getAttribute('data-product-handle');

          function doAddToCart(vid) {
            btn.disabled = true;
            btn.textContent = t.adding;
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: Number(vid), quantity: 1 })
            })
            .then(res => res.json())
            .then(data => {
              if (data.id) {
                btn.textContent = t.addedToCart;
                document.dispatchEvent(new CustomEvent('theme:update:cart'));
                document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
              } else {
                btn.textContent = '❌ ' + (data.description || t.cartError);
                btn.disabled = false;
              }
            })
            .catch(() => {
              btn.textContent = t.tryAgain;
              btn.disabled = false;
            });
          }

          if (variantId) {
            doAddToCart(variantId);
          } else if (productHandle) {
            btn.disabled = true;
            btn.textContent = t.findingWine;
            fetch('/products/' + productHandle + '.js')
              .then(r => r.json())
              .then(p => {
                const av = (p.variants || []).find(v => v.available) || (p.variants || [])[0];
                if (av) {
                  btn.setAttribute('data-variant-id', String(av.id));
                  doAddToCart(String(av.id));
                } else {
                  btn.textContent = t.notAvailable;
                  btn.disabled = false;
                }
              })
              .catch(() => {
                btn.textContent = t.notAvailable;
                btn.disabled = false;
              });
          } else {
            btn.textContent = t.notAvailable;
          }
        });
      });

      // Insert all children before the grid
      while (pairedContainer.firstChild) {
        grid.parentNode.insertBefore(pairedContainer.firstChild, grid);
      }

      // NOW hide the original grid after successful injection
      grid.style.display = 'none';

      // Remove delivery reassurance blocks on wishing-list page
      document.querySelectorAll('.vinpop-delivery-reassurance').forEach(el => el.remove());

      // Add ?from=tasting to all product links so the tasting menu persists on product pages
      document.querySelectorAll('.vinpop-paired-grid a[href*="/products/"]').forEach(a => {
        const url = new URL(a.href, window.location.origin);
        url.searchParams.set('from', 'tasting');
        a.href = url.pathname + url.search;
      });

      // Remove the FOUC-prevention style now that paired view is ready
      const hideStyle = document.getElementById('vinpop-wishlist-hide');
      if (hideStyle) hideStyle.remove();


      console.log('❤️ Wishing-list: Paired view injected successfully');
      pairedViewRendered = true;

      // Hide filters/sorting
      const filtersBar = document.querySelector('#native-filters-container');
      if (filtersBar) filtersBar.style.display = 'none';
      const resultsCount = document.querySelector('#facets-results-count');
      if (resultsCount) resultsCount.style.display = 'none';

      } catch (err) {
        // Paired view failed - show simple fallback
        console.error('❌ Wishing-list: Paired view failed, showing fallback', err);
        // Remove the FOUC-prevention style
        const hideStyle = document.getElementById('vinpop-wishlist-hide');
        if (hideStyle) hideStyle.remove();
        grid.style.display = 'none';
        const fallbackMsg = document.createElement('div');
        fallbackMsg.className = 'vinpop-wishing-msg';
        fallbackMsg.style.cssText = 'text-align:center;padding:3rem 1rem;';
        fallbackMsg.innerHTML = '<div style="font-size:2rem;margin-bottom:1rem;">🍷</div>' +
          '<p style="font-size:1.1rem;font-weight:600;">' + (isEN ? 'Your Playlist' : 'Je Playlist') + '</p>' +
          '<ul style="text-align:left;max-width:400px;margin:1rem auto;list-style:none;padding:0;">' +
          pairs.map(p => '<li style="padding:0.5rem 0;border-bottom:1px solid rgba(0,0,0,0.1);">🎭 ' + 
            (p.blind ? p.blind.title : 'Tasting') + 
            (p.real ? ' → 🍷 ' + p.real.title : '') + 
          '</li>').join('') +
          '</ul>' +
          '<p style="margin-top:1rem;opacity:0.7;font-size:0.9rem;">' + 
          (isEN ? 'Visit these products from the collection page.' : 'Bekijk deze producten vanuit de collectie pagina.') + '</p>';
        grid.parentNode.insertBefore(fallbackMsg, grid);
        pairedViewRendered = true;
      }

      wishFilterRunning = false;
    }

    async function initWishingList() {
      await filterWishingList();

      // Re-apply after AJAX filtering / DOM changes (with debounce)
      let wishDebounce = null;
      const wishGrid = document.querySelector('#facets-results');
      if (wishGrid) {
        const wishObserver = new MutationObserver(() => {
          clearTimeout(wishDebounce);
          wishDebounce = setTimeout(filterWishingList, 200);
        });
        wishObserver.observe(wishGrid, { childList: true, subtree: true });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(initWishingList, 300));
    } else {
      setTimeout(initWishingList, 300);
    }
  }

  // ==================== TOAST NOTIFICATION ====================
  function showToast(message) {
    // Remove existing toast
    const existing = document.querySelector('.vinpop-fav-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'vinpop-fav-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('vinpop-fav-toast--visible');
    });

    // Auto-hide after 2.5 seconds
    setTimeout(() => {
      toast.classList.remove('vinpop-fav-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  console.log('Favourites: System loaded. Logged in:', isLoggedIn, '| Tasting:', isTastingCollection, '| Wishing-list:', isWishingList);
})();

